import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Search, Loader2, RefreshCw, XCircle, CreditCard, Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import CustomerLayout from "@/components/customer/CustomerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import ReviewModal from "@/components/review/ReviewModal";
import PaymentBreakdown from "@/components/booking/PaymentBreakdown";
import PaymentMethodModal from "@/components/booking/PaymentMethodModal";
import PaymentTypeModal from "@/components/booking/PaymentTypeModal";
import PaymentHistoryModal from "@/components/booking/PaymentHistoryModal";
import CancelBookingModal from "@/components/booking/CancelBookingModal";
import RentalAgreementModal from "@/components/booking/RentalAgreementModal";
import {
  BOOKING_STATUS_LABELS, BOOKING_STATUS_STYLES, DURATION_UNIT_LABELS,
  PAYMENT_STATUS_LABELS, PAYMENT_STATUS_STYLES,
  REFUND_STATUS_LABELS, REFUND_STATUS_STYLES,
  formatRupiah, formatDate,
} from "@/components/booking/bookingMeta";

export default function CustomerBookingsPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [agreementBooking, setAgreementBooking] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [typeCtx, setTypeCtx] = useState(null);
  const [payCtx, setPayCtx] = useState(null);
  const [historyBooking, setHistoryBooking] = useState(null);

  const load = () => {
    setLoading(true);
    setError(false);
    apiClient
      .get("/customer/bookings")
      .then((r) => setBookings(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const pay = (booking) => setTypeCtx(booking);

  return (
    <CustomerLayout testid="customer-bookings-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Booking Saya</h1>
          <p className="mt-2 text-sm text-muted-foreground">Riwayat dan status pemesanan kamar kamu.</p>
        </div>
        <button onClick={load} data-testid="customer-bookings-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="customer-bookings-error">
          <p className="text-sm text-destructive">Gagal memuat booking.</p>
          <button onClick={load} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : bookings.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="customer-bookings-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><CalendarCheck size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada booking.</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">Jelajahi hunian dan pesan kamar impianmu.</p>
          <button onClick={() => navigate("/search")} data-testid="customer-bookings-explore-button"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-105 active:scale-95 transition-transform">
            <Search size={16} /> Jelajahi Hunian
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="customer-bookings-list">
          {bookings.map((b, i) => {
            const canPay = b.status === "approved" || b.status === "completed";
            const canCancel = b.status === "pending" || b.status === "approved";
            const hasAgreement = ["approved", "completed", "cancelled"].includes(b.status);
            const dpTooLow = (b.dp_rate ?? 20) < 20 && (b.room_amount_paid || 0) <= 0;
            return (
            <div key={b.id} data-testid={`customer-booking-${i}`}
              className="rounded-2xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold truncate">{b.property_name || "Hunian"}</h3>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${BOOKING_STATUS_STYLES[b.status] || ""}`} data-testid={`customer-booking-status-${i}`}>
                      {BOOKING_STATUS_LABELS[b.status] || b.status}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Kamar {b.room_number}{b.room_name ? ` · ${b.room_name}` : ""} · {b.duration} {DURATION_UNIT_LABELS[b.duration_unit]}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {formatDate(b.start_date)} → {formatDate(b.end_date)}
                  </p>
                  {b.status === "rejected" && b.rejection_reason && (
                    <p className="mt-2 text-sm text-rose-600">Alasan ditolak: {b.rejection_reason}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-heading text-xl font-bold text-primary" data-testid={`customer-booking-total-${i}`}>{formatRupiah(b.total_amount)}</p>
                  <p className="text-xs text-muted-foreground">Total kewajiban (kamar + deposit)</p>
                  {canCancel && (
                    <button onClick={() => setCancelBooking(b)}
                      data-testid={`customer-booking-cancel-${i}`}
                      className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors">
                      <XCircle size={14} /> Batalkan Booking
                    </button>
                  )}
                  {b.status === "cancelled" && b.refund_status && b.refund_status !== "none" && (
                    <div className="mt-3" data-testid={`customer-booking-refund-status-${i}`} data-refund-status={b.refund_status}>
                      <p className="text-xs text-muted-foreground">Status Refund</p>
                      <span className={`inline-block mt-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${REFUND_STATUS_STYLES[b.refund_status] || ""}`}>
                        {REFUND_STATUS_LABELS[b.refund_status] || b.refund_status}
                      </span>
                    </div>
                  )}
                  {b.status === "completed" && (
                    b.has_review ? (
                      <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600"
                        data-testid={`customer-booking-reviewed-${i}`}>
                        <CheckCircle2 size={14} /> Sudah direview
                      </p>
                    ) : (
                      <button onClick={() => setReviewBooking(b)}
                        data-testid={`customer-booking-review-${i}`}
                        className="mt-3 inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:scale-105 active:scale-95 transition-transform">
                        <Star size={14} /> Berikan Review
                      </button>
                    )
                  )}
                </div>
              </div>

              {canPay && <PaymentBreakdown b={b} idPrefix={`customer-booking-${i}`} />}

              {canPay && dpTooLow && b.room_payment_status !== "fully_paid" && (
                <p className="mt-3 text-xs text-rose-600" data-testid={`customer-booking-dp-low-${i}`}>
                  DP rate kamar ini di bawah 20% ({b.dp_rate}%). Pembayaran belum dapat dilakukan — hubungi pemilik.
                </p>
              )}

              {canPay && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.booking_payment_status !== "fully_paid" && !(dpTooLow && b.room_payment_status !== "fully_paid") && (
                    <button onClick={() => pay(b)}
                      data-testid={`customer-booking-pay-${i}`}
                      className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-105 active:scale-95 transition-transform">
                      <CreditCard size={14} /> Bayar
                    </button>
                  )}
                  <button onClick={() => setHistoryBooking(b)}
                    data-testid={`customer-booking-history-${i}`}
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold border border-border text-foreground hover:bg-secondary/60 transition-colors">
                    Riwayat Pembayaran
                  </button>
                </div>
              )}

              {hasAgreement && (
                <div className="mt-3 flex items-center gap-2 flex-wrap rounded-xl border border-border bg-secondary/10 px-3 py-2">
                  <span className="text-sm font-medium text-foreground">Rental Agreement</span>
                  <button onClick={() => setAgreementBooking(b)}
                    data-testid={`customer-booking-agreement-${i}`}
                    className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
                    Lihat Kontrak
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      <ReviewModal
        open={!!reviewBooking}
        booking={reviewBooking}
        onClose={() => setReviewBooking(null)}
        onSuccess={load}
      />

      <CancelBookingModal
        open={!!cancelBooking}
        booking={cancelBooking}
        onClose={() => setCancelBooking(null)}
        onSuccess={load}
      />

      <RentalAgreementModal
        open={!!agreementBooking}
        bookingId={agreementBooking?.id}
        role="customer"
        onClose={() => setAgreementBooking(null)}
        onAccepted={load}
      />

      <PaymentTypeModal
        open={!!typeCtx}
        booking={typeCtx}
        onClose={() => setTypeCtx(null)}
        onSelect={({ amount, purpose }) => {
          setPayCtx({ booking: typeCtx, amount, purpose });
          setTypeCtx(null);
        }}
      />

      <PaymentMethodModal
        open={!!payCtx}
        booking={payCtx?.booking}
        purpose={payCtx?.purpose}
        amount={payCtx?.amount}
        onClose={() => setPayCtx(null)}
        onBack={() => { setTypeCtx(payCtx.booking); setPayCtx(null); }}
      />

      <PaymentHistoryModal
        open={!!historyBooking}
        booking={historyBooking}
        onClose={() => setHistoryBooking(null)}
      />
    </CustomerLayout>
  );
}
